import { resetSession } from '@/features/authentication/authenticationSlice';
import { authApi } from '@/services/reparoly/authApi';
import { ordersApi } from '@/services/reparoly/ordersApi';
import { catalogApi } from '@/services/reparoly/catalogApi';
import { paymentApi } from '@/services/reparoly/paymentApi';
import { legalTermsApi } from '@/services/reparoly/legalTermsApi';
import { clientsApi } from '@/services/reparoly/clientsApi';
import { intakesApi } from '@/services/reparoly/intakesApi';
import { onboardingApi } from '@/services/reparoly/onboardingApi';
import { resetCompany } from '@/features/company/companySlice';
import { resetProfile } from '@/features/profile/profileSlice';
import { resetPaymentMethods } from '@/features/dashboard/settings/payment-methods/paymentMethodsSlice';
import { showAppLoading, hideAppLoading, resetAppShell } from '@/app/store/appShellSlice';
import { resetClientState } from '@/features/app/client/clientSlice';
import { resetEquipmentDataState } from '@/features/app/equipment-data/equipmentDataSlice';
import { resetSignatureConsentState } from '@/features/app/signature-consent/signatureConsentSlice';
import { resetReception } from '@/features/app/reception/receptionSlice';
import { resetTechnicians } from '@/features/users/techniciansSlice';
import { clearSuppliers } from '@/features/inventory/suppliers/suppliersSlice';
import { resetWarranties } from '@/features/dashboard/warranties/warrantiesSlice';
import { resetOrderStatuses } from '@/features/dashboard/settings/order-states/orderStatusesSlice';
import { resetSearch } from '@/store/slices/searchSlice';
import { clearSelectedOrder } from '@/features/app/selected-order/selectedOrderSlice';

type LogoutAndResetOptions = {
  dispatch: any;
  doLogout?: () => Promise<any>;
};

/**
 * Performs API logout (optional) and resets all persisted and API caches.
 * Callers are responsible for navigation after this function resolves.
 */
export async function logoutAndReset({ dispatch, doLogout }: LogoutAndResetOptions) {
  dispatch(showAppLoading());
  try {
    if (doLogout) await doLogout();
  } catch {
    // Error will be surfaced by AppNoticeDialog via RTK error handling; continue cleanup regardless
  } finally {
    try {
      if (typeof window !== 'undefined') {
        // Signal to auth guard to skip silent refresh and to UI to suppress error dialog
        sessionStorage.setItem('rep_skip_refresh', '1');
        sessionStorage.setItem('rep_skip_errors', '1');
      }
    } catch {}
    // 1) Mark auth as logged out
    dispatch(resetSession());
    // 2) Purge RTK Query caches
    dispatch(authApi.util.resetApiState());
    dispatch(ordersApi.util.resetApiState());
    dispatch(catalogApi.util.resetApiState());
    dispatch(paymentApi.util.resetApiState());
    dispatch(legalTermsApi.util.resetApiState());
    dispatch(clientsApi.util.resetApiState());
    dispatch(intakesApi.util.resetApiState());
    dispatch(onboardingApi.util.resetApiState());
    // 3) Reset persisted slices (amplio)
    dispatch(resetCompany());
    dispatch(resetProfile());
    dispatch(resetPaymentMethods());
    dispatch(resetClientState());
    dispatch(resetEquipmentDataState());
    dispatch(resetSignatureConsentState());
    dispatch(resetReception());
    dispatch(resetTechnicians());
    dispatch(clearSuppliers());
    dispatch(resetWarranties());
    dispatch(resetOrderStatuses());
    dispatch(resetSearch());
    dispatch(clearSelectedOrder());
    // 4) Reset UI root (mantiene idioma)
    dispatch(resetAppShell());
    dispatch(hideAppLoading());
  }
}

export async function resetGlobalStore ({ dispatch }: LogoutAndResetOptions) {
  // 1) Mark auth as logged out
  dispatch(resetSession());
  // 2) Purge RTK Query caches
  dispatch(authApi.util.resetApiState());
  dispatch(ordersApi.util.resetApiState());
  dispatch(catalogApi.util.resetApiState());
  dispatch(paymentApi.util.resetApiState());
  dispatch(legalTermsApi.util.resetApiState());
  dispatch(clientsApi.util.resetApiState());
  dispatch(intakesApi.util.resetApiState());
  dispatch(onboardingApi.util.resetApiState());
  // 3) Reset persisted slices (amplio)
  dispatch(resetCompany());
  dispatch(resetProfile());
  dispatch(resetPaymentMethods());
  dispatch(resetClientState());
  dispatch(resetEquipmentDataState());
  dispatch(resetSignatureConsentState());
  dispatch(resetReception());
  dispatch(resetTechnicians());
  dispatch(clearSuppliers());
  dispatch(resetWarranties());
  dispatch(resetOrderStatuses());
  dispatch(resetSearch());
  dispatch(clearSelectedOrder());
  // 4) Reset UI root (mantiene idioma)
  dispatch(resetAppShell());
}
